import React, {createContext, useContext, useEffect, useState, useCallback} from "react";

import * as authApi from "@modules/auth/authApi.js";
import {setUnauthorizedHandler} from "@/api/axios.js";

const AuthContext = createContext(null);

export function useAuth()
{
	return useContext(AuthContext);
}

export function AuthProvider({children})
{
	const [status, setStatus] = useState("loading"); // "loading" | "authed" | "anon"
	const [user, setUser] = useState(null);
	// true mentre il server non risponde (deploy/riavvio): la schermata di caricamento lo dice.
	const [serverUnavailable, setServerUnavailable] = useState(false);

	// Solo un errore di autenticazione vero porta al login. Server irraggiungibile, 5xx o
	// risposta senza utente (backend giu' durante un deploy) → si riprova finche' risponde,
	// senza buttare fuori una sessione ancora valida.
	const loadMe = useCallback(async () =>
	{
		for (let attempt = 0; ; attempt++)
		{
			try
			{
				const u = await authApi.me();
				setUser(u);
				setServerUnavailable(false);
				setStatus("authed");
				return;
			}
			catch (e)
			{
				const httpStatus = e?.response?.status;
				const transient = e?.code === "INVALID_USER_RESPONSE" || !e?.response || httpStatus >= 500;
				if (!transient)
				{
					setUser(null);
					setServerUnavailable(false);
					setStatus("anon");
					return;
				}
				setServerUnavailable(true);
				await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 5000)));
			}
		}
	}, []);

	useEffect(() =>
	{
		// Quando l'interceptor axios rileva una sessione non valida → torna anonimo.
		setUnauthorizedHandler(() =>
		{
			setUser(null);
			setStatus("anon");
		});
		loadMe();
	}, [loadMe]);

	// "Collegato" senza dati dell'utente non deve esistere: l'interfaccia restava senza menu e
	// rimbalzava fra due pagine bloccando la scheda (2026-10-21). Si torna al caricamento e si
	// ricontrolla la sessione: ne esce solo un utente valido, il login o "server non disponibile".
	const missingUser = status === "authed" && !user?.username;

	useEffect(() =>
	{
		if (!missingUser)
			return;
		setStatus("loading");
		loadMe();
	}, [missingUser, loadMe]);

	const login = useCallback(async (username, password, remember = true) =>
	{
		const u = await authApi.login(username, password, remember);
		setUser(u);
		setStatus("authed");
		return u;
	}, []);

	const logout = useCallback(async () =>
	{
		try { await authApi.logout(); }
		catch { /* idempotente */ }
		setUser(null);
		setStatus("anon");
	}, []);

	const hasMenu = useCallback((key) => !!user?.allowedMenuItems?.includes(key), [user]);

	const value = {
		// Finche' la sessione viene ricontrollata nessuno deve vedere lo stato "authed" vuoto.
		status: missingUser ? "loading" : status,
		serverUnavailable,
		user,
		login,
		logout,
		reload: loadMe,
		hasMenu,
		role: user?.role || null,
		allowedMenuItems: user?.allowedMenuItems || [],
		landingMenuItem: user?.landingMenuItem || null
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
